import React, { useEffect, useRef } from "react";
import { Box, Typography, Stack, IconButton, Container } from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useNavigate } from "react-router-dom";
import { projectNavigatePath } from "../../../../Utils/detailsNavigatePath";

gsap.registerPlugin(ScrollTrigger);

export default function TopProjects({ projects = [] }) {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef([]);

  // Keep only the first 4 projects
  const topFourProjects = projects.slice(0, 4);

  useEffect(() => {
    // Prevent animation if there is no data
    if (topFourProjects.length === 0) return;

    const ctx = gsap.context(() => {
      // Title Animation
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
          },
        },
      );

      // Cards Animation
      gsap.fromTo(
        cardsRef.current,
        {
          opacity: 0,
          y: 60,
          clipPath: "inset(10% 10% 10% 10% round 24px)",
          scale: 0.95,
        },
        {
          opacity: 1,
          y: 0,
          clipPath: "inset(0% 0% 0% 0% round 24px)",
          scale: 1,
          duration: 1.2,
          stagger: 0.15,
          ease: "expo.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%",
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [topFourProjects.length]);

  const items = topFourProjects.map((project, index) => {
    return (
      <Box
        onClick={() => navigate(projectNavigatePath(project))}
        key={project?.code || index}
        ref={(el) => (cardsRef.current[index] = el)}
        sx={{
          // Dynamic grid layout based on index directly in the return
          gridColumn: {
            xs: "span 12",
            lg: index === 0 ? "span 8" : index === 3 ? "span 12" : "span 4",
          },
          gridRow: {
            xs: "span 1",
            lg: index === 0 ? "span 2" : "span 1",
          },
          position: "relative",
          borderRadius: "24px",
          overflow: "hidden",
          cursor: "pointer",
          "&:hover .project-image": {
            transform: "scale(1.08)",
          },
          "&:hover .project-content": {
            transform: "translateY(-10px)",
          },
          "&:hover .project-icon": {
            transform: "translate(-5px, -5px)",
            bgcolor: "#00ffcc",
            color: "#000",
          },
        }}
      >
        {/* Background Image */}
        <Box
          className="project-image"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${import.meta.env.VITE_URL + project.image_path?.[0]})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transition: "transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />

        {/* Dark Gradient Overlay */}
        <Box
          className="project-overlay"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.1) 100%)",
            transition: "background 0.5s ease",
          }}
        />

        {/* Card Content */}
        <Stack
          className="project-content"
          justifyContent="space-between"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            p: { xs: 3, lg: 4 },
            boxSizing: "border-box",
            transition: "transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton
              aria-label="go to project"
              className="project-icon"
              sx={{
                bgcolor: "rgba(255,255,255,0.1)",
                color: "#fff",
                backdropFilter: "blur(12px)",
                transition: "all 0.4s ease",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              <ArrowOutwardIcon sx={{ transform: "rotate(-90deg)" }} />
            </IconButton>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: { xs: 12, lg: 13 },
                color: "#00ffcc",
                letterSpacing: "1px",
                mb: 1.5,
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              {project?.category}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 16, lg: 22 },
                fontWeight: 800,
                textShadow: "0px 4px 15px rgba(0,0,0,0.8)",
              }}
            >
              {project?.name}
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  });

  return (
    <Box>
      <Container
        maxWidth="xl"
        ref={sectionRef}
        sx={{
          py: { xs: 8, lg: 15 },
          color: "#fff",
          "& *": {
            direction: "rtl",
          },
        }}
      >
        {/* Header Section */}
        <Stack
          ref={titleRef}
          justifyContent="center"
          alignItems="center"
          mb={{ xs: 5, lg: 8 }}
          spacing={3}
        >
          <Typography
            sx={{
              fontSize: { xs: 30, lg: 44 },
              fontWeight: 700,
              letterSpacing: "-1px",
              lineHeight: 1.1,
              color: "secondary.main",
            }}
          >
            پروژه‌های برگزیده
          </Typography>
          <Typography
            sx={{
              opacity: 0.7,
              fontSize: { xs: 14, lg: 16 },
              lineHeight: 1.8,
            }}
          >
            خلاقیـت، مهندسـی دقیـق و تمرکـز بر تجربـه کاربـری. مجموعه‌ای از
            بهترین راهکارهای دیجیتال که برای آینده خلق کرده‌ایم.
          </Typography>
        </Stack>

        {/* Main Grid Container */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gridAutoRows: { xs: "250px", lg: "300px" },
            gap: { xs: 2, lg: 3 },
          }}
        >
          {items}
        </Box>
      </Container>
    </Box>
  );
}
